import { useCallback, useMemo, useState } from 'react'

import { FilterOptionsState } from '@mui/material'

import { SELECT_OPTION } from '../constants'
import { AutocompleteOption, TagsFieldValue } from '../types'

export function useAutocompleteManageProps(
  onChange: (values: TagsFieldValue) => void,
  value: TagsFieldValue,
  options: AutocompleteOption[],
) {
  // manage the input value of the autocomplete
  const [inputValue, setInputValue] = useState<string>('')

  const selectedOptions = useMemo(() => {
    if (!Array.isArray(value)) return []

    return options.filter((option: AutocompleteOption) => value.includes(option.value))
  }, [value, options])

  const getOptionLabel = (option: AutocompleteOption) => option.label

  const getOptionDisabled = (option: AutocompleteOption) => !!option.disabled

  const isOptionEqualToValue = (option: AutocompleteOption, value: AutocompleteOption) =>
    option.value === value?.value

  const filterOptions = (
    filtered: AutocompleteOption[],
    state: FilterOptionsState<AutocompleteOption>,
  ) => {
    const inputValue = state.inputValue?.trim().toLowerCase() || ''
    const finalFiltered = filtered.filter(option => option.label.toLowerCase().includes(inputValue))
    return [...finalFiltered, SELECT_OPTION]
  }

  const onChangeValue = (_, newValue: AutocompleteOption[]) => {
    const values = newValue.filter(opt => opt.value !== SELECT_OPTION.value).map(opt => opt.value)
    onChange(values)
  }

  const onChangeInput = (_, newInputValue: string) => {
    setInputValue(newInputValue)
  }

  const onClearInput = () => {
    setInputValue('')
  }

  const onSelectDeselectAllOptions = useCallback(
    (allOptionsSelected: boolean, selectableFilteredOptions: AutocompleteOption[]) => {
      //if all options are selected, deselect them, otherwise select all
      const updated = allOptionsSelected
        ? selectedOptions.filter(
            selected =>
              !selectableFilteredOptions.some(filtered => filtered.value === selected.value),
          )
        : [
            ...selectedOptions,
            ...selectableFilteredOptions.filter(
              option => !selectedOptions.some(selected => selected.value === option.value),
            ),
          ]
      const values = updated.map(option => option.value)
      onChange(values)
    },
    [onChange, selectedOptions],
  )

  return {
    inputValue,
    selectedOptions,
    isOptionEqualToValue,
    getOptionLabel,
    getOptionDisabled,
    filterOptions,
    onChangeValue,
    onChangeInput,
    onClearInput,
    onSelectDeselectAllOptions,
  }
}
